import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRicosV1RicosServicesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ricos-services_services';

/**
 * Validates a Ricos document against a set of rules and optionally fixes any violations
 * This method checks if the provided document structure conforms to the Ricos format
 * and can optionally attempt to fix any issues found
 */
export function validateDocument(payload: object): RequestOptionsFactory<any> {
  function __validateDocument({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ricos.v1.service',
      method: 'POST' as any,
      methodFqn: 'wix.ricos.v1.RicosServices.ValidateDocument',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRicosV1RicosServicesUrl({
        protoPath: '/v1/validate',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'validDocument.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'validDocument.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'validDocument.nodes.galleryData.items.video.thumbnail.duration',
              },
              { path: 'validDocument.nodes.galleryData.options.item.ratio' },
              { path: 'validDocument.nodes.imageData.image.duration' },
              { path: 'validDocument.nodes.mapData.mapSettings.lat' },
              { path: 'validDocument.nodes.mapData.mapSettings.lng' },
              { path: 'validDocument.nodes.pollData.poll.image.duration' },
              {
                path: 'validDocument.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'validDocument.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'validDocument.nodes.appEmbedData.image.duration' },
              { path: 'validDocument.nodes.videoData.video.duration' },
              { path: 'validDocument.nodes.videoData.thumbnail.duration' },
              { path: 'validDocument.nodes.audioData.audio.duration' },
              { path: 'validDocument.nodes.audioData.coverImage.duration' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'validDocument.metadata.createdTimestamp' },
              { path: 'validDocument.metadata.updatedTimestamp' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __validateDocument;
}

/** Convert content from various formats (Markdown, HTML, Plain text) to Ricos document format */
export function convertToRicosDocument(
  payload: object
): RequestOptionsFactory<any> {
  function __convertToRicosDocument({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ricos.v1.service',
      method: 'POST' as any,
      methodFqn: 'wix.ricos.v1.RicosServices.ConvertToRicosDocument',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRicosV1RicosServicesUrl({
        protoPath: '/v1/convert/to-ricos',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'document.nodes.galleryData.items.image.media.duration' },
              { path: 'document.nodes.galleryData.items.video.media.duration' },
              {
                path: 'document.nodes.galleryData.items.video.thumbnail.duration',
              },
              { path: 'document.nodes.galleryData.options.item.ratio' },
              { path: 'document.nodes.imageData.image.duration' },
              { path: 'document.nodes.mapData.mapSettings.lat' },
              { path: 'document.nodes.mapData.mapSettings.lng' },
              { path: 'document.nodes.pollData.poll.image.duration' },
              { path: 'document.nodes.pollData.poll.options.image.duration' },
              {
                path: 'document.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'document.nodes.appEmbedData.image.duration' },
              { path: 'document.nodes.videoData.video.duration' },
              { path: 'document.nodes.videoData.thumbnail.duration' },
              { path: 'document.nodes.audioData.audio.duration' },
              { path: 'document.nodes.audioData.coverImage.duration' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'document.metadata.createdTimestamp' },
              { path: 'document.metadata.updatedTimestamp' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __convertToRicosDocument;
}

/** Convert content from Ricos document format to various formats (Markdown, HTML, Plain text) */
export function convertFromRicosDocument(
  payload: object
): RequestOptionsFactory<any> {
  function __convertFromRicosDocument({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'document.nodes.galleryData.items.image.media.duration' },
          { path: 'document.nodes.galleryData.items.video.media.duration' },
          { path: 'document.nodes.galleryData.items.video.thumbnail.duration' },
          { path: 'document.nodes.galleryData.options.item.ratio' },
          { path: 'document.nodes.imageData.image.duration' },
          { path: 'document.nodes.mapData.mapSettings.lat' },
          { path: 'document.nodes.mapData.mapSettings.lng' },
          { path: 'document.nodes.pollData.poll.image.duration' },
          { path: 'document.nodes.pollData.poll.options.image.duration' },
          {
            path: 'document.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'document.nodes.appEmbedData.image.duration' },
          { path: 'document.nodes.videoData.video.duration' },
          { path: 'document.nodes.videoData.thumbnail.duration' },
          { path: 'document.nodes.audioData.audio.duration' },
          { path: 'document.nodes.audioData.coverImage.duration' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'document.metadata.createdTimestamp' },
          { path: 'document.metadata.updatedTimestamp' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ricos.v1.service',
      method: 'POST' as any,
      methodFqn: 'wix.ricos.v1.RicosServices.ConvertFromRicosDocument',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRicosV1RicosServicesUrl({
        protoPath: '/v1/convert/from-ricos',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __convertFromRicosDocument;
}
